import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  TextField,
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  TableChart as TableChartIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

interface Evaluation {
  id: string;
  name: string;
  fullName: string;
  description: string;
  category: string;
  icon: React.ReactNode;
  status: 'active' | 'inactive';
}

const evaluations: Evaluation[] = [
  // Matriz siempre primero
  {
    id: 'matrix',
    name: 'Matriz de Conversión',
    fullName: 'Matriz de Conversión de Puntajes',
    description: 'Tabla de referencia para convertir percentiles a diferentes tipos de puntajes (Z, T, Estándar, Escalar) y clasificaciones de rendimiento.',
    category: 'Referencia',
    icon: <TableChartIcon />,
    status: 'active'
  },
  // Activos ordenados alfabéticamente
  {
    id: 'boston-naming',
    name: 'Boston Naming Test',
    fullName: 'Boston Naming Test (BNT)',
    description: 'Evaluación de la capacidad de denominación visual y acceso léxico. El paciente debe nombrar objetos presentados en imágenes.',
    category: 'Lenguaje',
    icon: <PsychologyIcon />,
    status: 'active'
  },
  {
    id: 'd2',
    name: 'D2',
    fullName: 'Test de Atención D2',
    description: 'Evaluación de atención selectiva y concentración',
    category: 'Atención',
    icon: <SpeedIcon />,
    status: 'active'
  },
  {
    id: 'elevator-appendix-3b',
    name: 'Conteo de Elevator 3B',
    fullName: 'Conteo de Elevator con Distracción - Apéndice 3B',
    description: 'Equivalencias percentilares aproximadas por puntaje bruto para versiones A y B. Apéndice 3A no incluido.',
    category: 'Atención',
    icon: <SpeedIcon />,
    status: 'active'
  },
  {
    id: 'fluencias',
    name: 'Fluencias',
    fullName: 'Test de Fluencias',
    description: 'Evaluación de fluencia verbal semántica (animales) y fonológica (letras F, A, S). El paciente debe generar palabras según categorías específicas.',
    category: 'Lenguaje',
    icon: <PsychologyIcon />,
    status: 'active'
  },
  {
    id: 'hvlt-r',
    name: 'HVLT-R',
    fullName: 'Hopkins Verbal Learning Test - Revised',
    description: 'Evaluación de memoria verbal y aprendizaje. Incluye Recuerdo Total y Recuerdo Tardío.',
    category: 'Memoria',
    icon: <MemoryIcon />,
    status: 'active'
  },
  {
    id: 'ifs-ch',
    name: 'IFS-Ch',
    fullName: 'IFS-Ch (INECO Frontal Screening - Chile)',
    description: 'Tabla de referencia con la media y desviación estándar de sujetos control por subtest. No incluye percentiles ni grupos etarios/educacionales.',
    category: 'Función Ejecutiva',
    icon: <PsychologyIcon />,
    status: 'active'
  },
  {
    id: 'moca',
    name: 'MOCA',
    fullName: 'Montreal Cognitive Assessment',
    description: 'Cribado cognitivo global mediante puntaje directo total (0-30 puntos). El percentil se calcula automáticamente según la edad del paciente.',
    category: 'Cribado Cognitivo Global',
    icon: <PsychologyIcon />,
    status: 'active'
  },
  {
    id: 'rey-figure',
    name: 'Figura Compleja de Rey',
    fullName: 'Test de Figura Compleja de Rey-Osterrieth',
    description: 'Evaluación de la función visuoespacial y memoria visual mediante la copia y reproducción de memoria de una figura compleja. Incluye 4 condiciones: Exactitud y riqueza de Copia, Tiempo de Copia, Tipo de construcción en Reproducción de Memoria, y Exactitud y riqueza de Reproducción de Memoria.',
    category: 'Visuoespacial',
    icon: <PsychologyIcon />,
    status: 'active'
  },
  {
    id: 'stroop',
    name: 'Stroop',
    fullName: 'Test de Stroop',
    description: 'Evaluación de control inhibitorio y atención selectiva. Incluye 4 condiciones: Palabra, Color, Palabra-Color e Interferencia.',
    category: 'Control Inhibitorio',
    icon: <MemoryIcon />,
    status: 'active'
  },
  {
    id: 'tmt-a',
    name: 'TMT-A',
    fullName: 'Trail Making Test - Parte A',
    description: 'Evaluación de velocidad de procesamiento y atención visual. El paciente debe conectar números del 1 al 25 en orden secuencial.',
    category: 'Atención y Velocidad',
    icon: <SpeedIcon />,
    status: 'active'
  },
  {
    id: 'tmt-b',
    name: 'TMT-B',
    fullName: 'Trail Making Test - Parte B',
    description: 'Evaluación de flexibilidad cognitiva y función ejecutiva. El paciente debe alternar entre números y letras en orden secuencial.',
    category: 'Función Ejecutiva',
    icon: <PsychologyIcon />,
    status: 'active'
  },
  // Inactivos ordenados alfabéticamente
  {
    id: 'ace-r',
    name: 'ACE-R',
    fullName: 'Addenbrooke\'s Cognitive Examination - Revised',
    description: 'Evaluación cognitiva completa y detallada',
    category: 'Evaluación Cognitiva',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'aq',
    name: 'AQ',
    fullName: 'Coeficiente de Espectro Autista',
    description: 'Evaluación del coeficiente de espectro autista',
    category: 'Emocional',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'bta',
    name: 'BTA',
    fullName: 'Brief Test of Attention',
    description: 'Evaluación breve de atención',
    category: 'Atención',
    icon: <SpeedIcon />,
    status: 'inactive'
  },
  {
    id: 'bvmt',
    name: 'BVMT',
    fullName: 'Brief Visuospatial Memory Test',
    description: 'Evaluación de memoria visuoespacial',
    category: 'Memoria',
    icon: <MemoryIcon />,
    status: 'inactive'
  },
  {
    id: 'dass-21',
    name: 'DASS-21',
    fullName: 'Depression Anxiety Stress Scales - 21',
    description: 'Evaluación de depresión, ansiedad y estrés',
    category: 'Emocional',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'dkefs-sorting',
    name: 'D-KEFS (Sorting Card Test)',
    fullName: 'Delis-Kaplan Executive Function System - Sorting Card Test',
    description: 'Evaluación de función ejecutiva y categorización',
    category: 'Función Ejecutiva',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'dsm-48',
    name: 'DSM-48',
    fullName: 'DSM-48',
    description: 'Evaluación psicológica',
    category: 'Evaluación Cognitiva',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'eq',
    name: 'EQ',
    fullName: 'Coeficiente de Empatía',
    description: 'Evaluación del coeficiente de empatía',
    category: 'Emocional',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'fab',
    name: 'FAB',
    fullName: 'Frontal Assessment Battery',
    description: 'Evaluación de función ejecutiva frontal',
    category: 'Función Ejecutiva',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'hayling',
    name: 'Hayling Test',
    fullName: 'Hayling Test',
    description: 'Evaluación de control inhibitorio y función ejecutiva',
    category: 'Control Inhibitorio',
    icon: <MemoryIcon />,
    status: 'inactive'
  },
  {
    id: 'ifs',
    name: 'IFS',
    fullName: 'INECO Frontal Screening',
    description: 'Evaluación de función ejecutiva frontal',
    category: 'Función Ejecutiva',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'iri',
    name: 'IRI',
    fullName: 'Interpersonal Reactivity Index',
    description: 'Evaluación de empatía y teoría de la mente',
    category: 'Emocional',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'mini-sea',
    name: 'MINI-SEA',
    fullName: 'Mini-SEA',
    description: 'Evaluación de cognición social',
    category: 'Cognición Social',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'mmse',
    name: 'MMSE',
    fullName: 'Mini-Mental State Examination',
    description: 'Evaluación cognitiva breve y estandarizada',
    category: 'Evaluación Cognitiva',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'pasat',
    name: 'PASAT',
    fullName: 'Paced Auditory Serial Addition Test',
    description: 'Evaluación de atención sostenida y velocidad de procesamiento',
    category: 'Atención',
    icon: <SpeedIcon />,
    status: 'inactive'
  },
  {
    id: 'qolie-31',
    name: 'QOLIE-31',
    fullName: 'Quality of Life in Epilepsy - 31',
    description: 'Evaluación de calidad de vida en epilepsia',
    category: 'Calidad de Vida',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'reloj',
    name: 'Test del Reloj',
    fullName: 'Test del Reloj',
    description: 'Evaluación de función ejecutiva y visuoespacial',
    category: 'Función Ejecutiva',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'retencion-digitos-wais',
    name: 'Retención de Dígitos (WAIS-IV)',
    fullName: 'Test de Retención de Dígitos - WAIS-IV',
    description: 'Evaluación de memoria de trabajo y atención',
    category: 'Memoria y Atención',
    icon: <MemoryIcon />,
    status: 'inactive'
  },
  {
    id: 'rey-old',
    name: 'Figura Compleja de Rey (antigua)',
    fullName: 'Test de Figura Compleja de Rey-Osterrieth (antigua)',
    description: 'Evaluación de función visuoespacial y memoria (versión antigua)',
    category: 'Visuoespacial',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'secuenciacion-letras-numeros',
    name: 'Secuenciación Letras-Números (WAIS-IV)',
    fullName: 'Test de Secuenciación Letras-Números - WAIS-IV',
    description: 'Evaluación de memoria de trabajo y atención',
    category: 'Memoria y Atención',
    icon: <MemoryIcon />,
    status: 'inactive'
  },
  {
    id: 'smdt',
    name: 'SMDT',
    fullName: 'Test de Símbolos y Dígitos',
    description: 'Evaluación de velocidad de procesamiento',
    category: 'Atención y Velocidad',
    icon: <SpeedIcon />,
    status: 'inactive'
  },
  {
    id: 'tol',
    name: 'ToL',
    fullName: 'Tower of London',
    description: 'Evaluación de planificación y función ejecutiva',
    category: 'Función Ejecutiva',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'vat',
    name: 'VAT',
    fullName: 'Visual Association Test',
    description: 'Evaluación de memoria y asociación visual',
    category: 'Memoria',
    icon: <MemoryIcon />,
    status: 'inactive'
  },
  {
    id: 'wais-iv',
    name: 'WAIS-IV',
    fullName: 'Wechsler Adult Intelligence Scale - IV',
    description: 'Evaluación completa de inteligencia en adultos',
    category: 'Inteligencia',
    icon: <PsychologyIcon />,
    status: 'inactive'
  },
  {
    id: 'wisconsin',
    name: 'Modified - Wisconsin Card Sorting Test',
    fullName: 'Modified Wisconsin Card Sorting Test',
    description: 'Evaluación de flexibilidad cognitiva y función ejecutiva',
    category: 'Función Ejecutiva',
    icon: <PsychologyIcon />,
    status: 'inactive'
  }
];

const EvaluationsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const getEvaluationPath = (evaluationId: string) =>
    evaluationId === 'matrix' ? '/evaluations/matrix' : `/evaluations/${evaluationId}`;

  // Filtrar evaluaciones basado en la búsqueda
  const filteredEvaluations = useMemo(() => {
    if (!searchQuery.trim()) {
      return evaluations;
    }

    const query = searchQuery.toLowerCase().trim();
    return evaluations.filter((evaluation) => {
      const nameMatch = evaluation.name.toLowerCase().includes(query);
      const fullNameMatch = evaluation.fullName.toLowerCase().includes(query);
      const categoryMatch = evaluation.category.toLowerCase().includes(query);
      const descriptionMatch = evaluation.description.toLowerCase().includes(query);
      
      return nameMatch || fullNameMatch || categoryMatch || descriptionMatch;
    });
  }, [searchQuery]);

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3, mb: 2 }}>
          <Box sx={{ flex: '0 0 auto' }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Evaluaciones Neuropsicológicas
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Selecciona una evaluación para ver sus baremos y configuraciones
            </Typography>
          </Box>

          {/* Buscador */}
          <Box sx={{ minWidth: '300px', maxWidth: '400px', flex: '0 0 auto' }}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, categoría o descripción..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              size="small"
            />
            {searchQuery && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {filteredEvaluations.length} {filteredEvaluations.length === 1 ? 'evaluación encontrada' : 'evaluaciones encontradas'}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {filteredEvaluations.length > 0 ? (
          filteredEvaluations.map((evaluation) => (
          <Box sx={{ width: '300px', flexShrink: 0, display: 'flex' }} key={evaluation.id}>
            <Card
              {...(evaluation.status === 'active'
                ? { component: RouterLink, to: getEvaluationPath(evaluation.id) }
                : {})}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: evaluation.status === 'active' ? 'pointer' : 'default',
                opacity: evaluation.status === 'inactive' ? 0.6 : 1,
                textDecoration: 'none',
                color: 'inherit',
                transition: 'all 0.2s ease-in-out',
                '&:hover': evaluation.status === 'active' ? {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                } : {}
              }}
            >
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    color: 'primary.main', 
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {evaluation.icon}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h2" gutterBottom>
                      {evaluation.name}
                    </Typography>
                    <Chip 
                      label={evaluation.status === 'active' ? 'Disponible' : 'Próximamente'} 
                      color={evaluation.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                  {evaluation.description}
                </Typography>

                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    <strong>Categoría:</strong> {evaluation.category}
                  </Typography>
                  
                  {evaluation.status === 'active' && (
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ mt: 1 }}
                    >
                      {evaluation.id === 'matrix' ? 'Ver Matriz de Puntaje' : 'Ver Baremos'}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))
        ) : (
          <Box sx={{ width: '100%', textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No se encontraron evaluaciones
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Intenta con otros términos de búsqueda
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EvaluationsPage; 